import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { useThemeStore, type Theme } from "../../stores/themeStore";
import { Button } from "../ui/Button";
import {
  Map,
  LogOut,
  User,
  Users,
  Package,
  Briefcase,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  ChevronDown,
  Settings,
  TrendingDown,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cn } from "../../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../../assets/logo.svg";
import LogoIcon from "../../assets/logoicon.svg";

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Chiaro", icon: Sun },
  { value: "system", label: "Sistema", icon: Monitor },
  { value: "dark", label: "Scuro", icon: Moon },
];

export function Navbar() {
  const { user, isAuthenticated, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Close user menu when clicking outside (supports both mouse and touch)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <nav className="sticky top-0 z-[100] overlay-solid border-b border-transparent hover:border-[#284899]/40 transition-colors duration-200 flex-shrink-0">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src={Logo}
              alt="TDH logo"
              className="h-10 w-auto hidden sm:block"
              loading="lazy"
            />
            <img
              src={LogoIcon}
              alt="TDH logo"
              className="h-10 w-auto sm:hidden"
              loading="lazy"
            />
            <span className="sr-only">Destination Plan Machine</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {isAuthenticated && (
              <>
                <div className="relative group">
                  <Link to="/map">
                    <Button variant={isActive("/map") ? "default" : "ghost"} size="sm">
                      <Map className="h-4 w-4 mr-2" />
                      Mappa
                    </Button>
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-[hsl(var(--card))] backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl text-xs text-muted-foreground w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                    <p className="font-medium text-foreground mb-1">Mappa Operatori</p>
                    <p>Cerca operatori su mappa interattiva in base a territorio, periodo e categoria.</p>
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[hsl(var(--card))] border-l border-t border-white/10 rotate-45" />
                  </div>
                </div>
                <div className="relative group">
                  <Link to="/networking">
                    <Button variant={isActive("/networking") ? "default" : "ghost"} size="sm">
                      <Users className="h-4 w-4 mr-2" />
                      Networking
                    </Button>
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-[hsl(var(--card))] backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl text-xs text-muted-foreground w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                    <p className="font-medium text-foreground mb-1">Networking</p>
                    <p>Contatta altri operatori per creare pianificazioni congiunte e collaborazioni.</p>
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[hsl(var(--card))] border-l border-t border-white/10 rotate-45" />
                  </div>
                </div>
                <div className="relative group">
                  <Link to="/packages">
                    <Button variant={isActive("/packages") ? "default" : "ghost"} size="sm">
                      <Package className="h-4 w-4 mr-2" />
                      Pianifica
                    </Button>
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-[hsl(var(--card))] backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl text-xs text-muted-foreground w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                    <p className="font-medium text-foreground mb-1">Pianificazione Territoriale</p>
                    <p>Organizza su calendario le esperienze territoriali e genera PDF stampabili</p>
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[hsl(var(--card))] border-l border-t border-white/10 rotate-45" />
                  </div>
                </div>
                <div className="relative group">
                  <Link to="/services">
                    <Button variant={isActive("/services") ? "default" : "ghost"} size="sm">
                      <Briefcase className="h-4 w-4 mr-2" />
                      I miei servizi
                    </Button>
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-[hsl(var(--card))] backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl text-xs text-muted-foreground w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                    <p className="font-medium text-foreground mb-1">I miei servizi</p>
                    <p>Gestisci i tuoi servizi da usare nelle pianificazioni</p>
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[hsl(var(--card))] border-l border-t border-white/10 rotate-45" />
                  </div>
                </div>
                <div className="relative group">
                  <Link to="/seasonality">
                    <Button variant={isActive("/seasonality") ? "default" : "ghost"} size="sm">
                      <TrendingDown className="h-4 w-4 mr-2" />
                      Stagionalità
                    </Button>
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-[hsl(var(--card))] backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl text-xs text-muted-foreground w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                    <p className="font-medium text-foreground mb-1">Strumenti per destagionalizzare</p>
                    <p>Analizza i periodi a bassa domanda e ricevi suggerimenti per destagionalizzare.</p>
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-[hsl(var(--card))] border-l border-t border-white/10 rotate-45" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors",
                    "hover:bg-secondary/50",
                    isUserMenuOpen && "bg-secondary/50"
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-[#284899]/20 flex items-center justify-center">
                    <User className="h-4 w-4 text-[#284899]" />
                  </div>
                  <div className="text-sm text-left">
                    <p className="font-medium">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                      {user?.email}
                    </p>
                  </div>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-muted-foreground transition-transform",
                      isUserMenuOpen && "rotate-180"
                    )}
                  />
                </button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-72 rounded-xl shadow-2xl overflow-hidden border border-white/10 bg-[hsl(var(--card))] backdrop-blur-xl z-[9999]"
                    >
                      {/* User Info Header */}
                      <div className="p-4 border-b border-border bg-secondary/30">
                        <p className="font-medium truncate">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {user?.email}
                        </p>
                        {user?.role && (
                          <span className="inline-flex mt-2 px-2 py-0.5 text-xs font-medium rounded-full bg-[#284899]/20 text-[#284899]">
                            {user.role === "LR" ? "Rappresentante Legale" : user.role}
                          </span>
                        )}
                      </div>

                      {/* Theme Selector */}
                      <div className="p-4 border-b border-border">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Tema
                        </p>
                        <div className="flex gap-2 p-1 rounded-lg bg-secondary/30">
                          {themeOptions.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setTheme(option.value)}
                              className={cn(
                                "flex-1 flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all",
                                theme === option.value
                                  ? "bg-[#284899] text-white"
                                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                              )}
                            >
                              <option.icon className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="truncate">{option.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="p-2">
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            // Navigate to settings if we have that page
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                        >
                          <Settings className="h-4 w-4" />
                          Impostazioni
                        </button>
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <LogOut className="h-4 w-4" />
                          Esci
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to="/login">
                <Button>Accedi</Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden touch-manipulation"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Chiudi menu" : "Apri menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "md:hidden glass border-t border-white/5 overflow-hidden transition-all duration-300",
          isMobileMenuOpen ? "max-h-[calc(100vh-4rem)]" : "max-h-0"
        )}
      >
        <div className="px-4 py-4 space-y-3 overflow-y-auto max-h-[calc(100vh-4rem)]">
          {isAuthenticated ? (
            <>
              <div className="space-y-1 pb-1">
                <Link to="/map" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button variant={isActive("/map") ? "default" : "ghost"} className="w-full justify-start min-h-[44px]">
                    <Map className="h-4 w-4 mr-2 flex-shrink-0" />
                    Mappa
                  </Button>
                </Link>
                <p className="text-[10px] text-muted-foreground px-3 leading-relaxed">
                  Cerca operatori su mappa in base a territorio e periodo
                </p>
              </div>
              <div className="space-y-1 pb-1">
                <Link to="/networking" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button variant={isActive("/networking") ? "default" : "ghost"} className="w-full justify-start min-h-[44px]">
                    <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                    Networking
                  </Button>
                </Link>
                <p className="text-[10px] text-muted-foreground px-3 leading-relaxed">
                  Collabora con altri operatori e crea partnership
                </p>
              </div>
              <div className="space-y-1 pb-1">
                <Link to="/packages" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button variant={isActive("/packages") ? "default" : "ghost"} className="w-full justify-start min-h-[44px]">
                    <Package className="h-4 w-4 mr-2 flex-shrink-0" />
                    Pianifica
                  </Button>
                </Link>
                <p className="text-[10px] text-muted-foreground px-3 leading-relaxed">
                  Organizza esperienze territoriali e genera PDF
                </p>
              </div>
              <div className="space-y-1 pb-1">
                <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button variant={isActive("/services") ? "default" : "ghost"} className="w-full justify-start min-h-[44px]">
                    <Briefcase className="h-4 w-4 mr-2 flex-shrink-0" />
                    I miei servizi
                  </Button>
                </Link>
                <p className="text-[10px] text-muted-foreground px-3 leading-relaxed">
                  Servizi riutilizzabili per le pianificazioni
                </p>
              </div>
              <div className="space-y-1 pb-1">
                <Link to="/seasonality" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button variant={isActive("/seasonality") ? "default" : "ghost"} className="w-full justify-start min-h-[44px]">
                    <TrendingDown className="h-4 w-4 mr-2 flex-shrink-0" />
                    Stagionalità
                  </Button>
                </Link>
                <p className="text-[10px] text-muted-foreground px-3 leading-relaxed">
                  Analisi periodi bassa domanda
                </p>
              </div>

              {/* Mobile Theme Selector */}
              <div className="px-3 py-2">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Tema
                </p>
                <div className="flex gap-1.5 p-1 rounded-lg bg-secondary/30">
                  {themeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setTheme(option.value)}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all min-h-[44px] touch-manipulation",
                        theme === option.value
                          ? "bg-[#284899] text-white active:bg-[#1e3570]"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 active:bg-secondary/70"
                      )}
                    >
                      <option.icon className="h-4 w-4 flex-shrink-0" />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-border my-3" />
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full bg-[#284899]/20 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-[#284899]" />
                  </div>
                  <div className="text-sm min-w-0">
                    <p className="font-medium truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    {user?.email && (
                      <p className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </p>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="min-h-[44px] flex-shrink-0">
                  <LogOut className="h-4 w-4 mr-2" />
                  <span className="hidden xs:inline">Esci</span>
                </Button>
              </div>
            </>
          ) : (
            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block">
              <Button className="w-full min-h-[44px]">Accedi</Button>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
